import { useEnvContext } from "context/EnvContext";
import {
  APP_CAREPROVIDER,
  FILE_TABLE_ACTION_UPLOAD,
  FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
  FILE_TABLE_ACTION_UPLOAD_HIS,
  TRACK_EVENTS,
} from "core/consts";
import { PATHS } from "core/consts/pages";
import { useExtractDocs } from "core/model/api/endpoints/extractDocs/context";
import { useIsDocsAndExtractActive } from "core/model/careseekers";
import { processDocumentFile, validateFile } from "core/model/files";
import { ALLOWED_FILE_MIME_TYPES } from "core/model/files/config";
import {
  AuctionRequest,
  FileHISStateful,
  FileState,
  Filev2,
  QueryProgress,
} from "core/types";
import { Button, NonIconOnlyButtonProps } from "ds/ui/button/button";
import RSButton from "ds_legacy/components/RSButton";
import {
  GeneralListAction,
  SetAction,
} from "ds_legacy/components/Tables/GeneralTable/MenuActions";
import { HorizontalLayout, VerticalLayout } from "ds_legacy/materials/layouts";
import { dp } from "ds_legacy/materials/metrics";
import { useMedia } from "dsl/atoms/ResponsiveMedia";
import { useToast } from "dsl/atoms/ToastNotificationContext";
import { FileFeatureData } from "dsl/ecosystems/PatientFiles/useFilesFeature";
import { useIsEligiblePage } from "dsl/ecosystems/PatientFiles/useUploadFile";
import { PRODUCT_TOURS, tourAttributes } from "dsl/molecules/useProductTour";
import FileUploadHISModal from "dsl/organisms/Modals/FileUploadHISModal";
import {
  FileUploadModal,
  FileUploadModalProps,
  FileUploadSubmitHandler,
} from "dsl/organisms/Modals/FileUploadModal";
import { FileUploadModalDragNDrop } from "dsl/organisms/Modals/FileUploadModalDragNDrop";
import { FileUpIcon, LinkIcon } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useMatch } from "react-router-dom";
import { useTracking } from "react-tracking";
import { useLoggedCareseekerConfig } from "reduxentities/hooks";
import { useTranslations } from "translations";

export type ProcessedFile = {
  content: string;
  file: File;
};

export function useFileUpload() {
  const { trackEvent } = useTracking();
  const toast = useToast();
  const isEligiblePage = !!useMatch(`${PATHS.APP.PATIENT_REHAB_FORMS_HOME}/*`);

  const translations = useTranslations();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [processedFile, setProcessedFile] = useState<ProcessedFile | null>(
    null,
  );

  function handleClick() {
    if (inputRef.current) {
      inputRef.current.click();
    }
  }
  async function handleChange(file: File | undefined) {
    try {
      if (!file) {
        throw new Error(translations.fileUploader.fileUploadError);
      }

      await validateFile({ file, isEligiblePage, trackEvent, translations });

      const content = await processDocumentFile(file);
      setProcessedFile({ content, file });
    } catch (error) {
      toast({ color: "danger", message: (error as Error).message });
    }
  }

  function reset() {
    if (inputRef.current) {
      inputRef.current.value = "";
      setProcessedFile(null);
    }
  }

  return { handleChange, handleClick, inputRef, processedFile, reset };
}

export function FileUploaderV2({
  action,
  allowedFileTypes,
  availableFiles,
  getFilesHISProgress,
  handleSubmit,
  isDisabled = true,
  isMessenger,
  mutationProgress,
  reset,
  setAction,
  tooltip,
  unavailableFiles,
  uploadButtonProps,
}: {
  action: GeneralListAction<Filev2> | undefined;
  allowedFileTypes?: readonly string[] | string[];
  auctionRequest?: AuctionRequest;
  availableFiles: FileState[] | null;
  getFilesHISProgress: QueryProgress;
  handleSubmit: FileUploadSubmitHandler;
  isDisabled: boolean;
  isMessenger?: boolean;
  mutationProgress: QueryProgress;
  reset: () => void;
  setAction: SetAction<FileFeatureData>;
  tooltip?: string;
  unavailableFiles: FileHISStateful[] | null;
  uploadButtonProps?: Omit<NonIconOnlyButtonProps, "ref" | "onPress">;
}) {
  const { app } = useEnvContext();
  const { isTablet } = useMedia();
  const translations = useTranslations();
  const { trackEvent } = useTracking();
  const hasActiveOnPrem = useLoggedCareseekerConfig(
    "on_premise_archive_integration_enabled",
  );
  const {
    handleChange,
    handleClick,
    inputRef,
    processedFile,
    reset: resetFile,
  } = useFileUpload();

  const isEligiblePage = useIsEligiblePage();
  const { isDischargeLetterActive, isDocsAndExtractActive } =
    useIsDocsAndExtractActive();
  const showExtractCheckbox =
    (isDocsAndExtractActive || isDischargeLetterActive) && !isEligiblePage;
  const { extractOcr } = useExtractDocs();

  async function submit(
    fileUploadData: Parameters<FileUploadModalProps["handleSubmit"]>[0],
  ) {
    handleSubmit(
      { ...processedFile, ...fileUploadData },
      {
        onComplete: async (file) => {
          if (fileUploadData.should_extract_on_upload && processedFile?.file) {
            await extractOcr.handleOcrForFile({
              file: processedFile.file,
              fileCategory: fileUploadData.category,
              id: file.id,
            });
          }
        },
        onStarted: () => {
          reset();
          resetFile();
        },
      },
    );
  }

  const close = useCallback(() => {
    reset();
    resetFile();
  }, []);

  const handleSelectFileV2 = useCallback(() => {
    if (app === APP_CAREPROVIDER) {
      if (isTablet) {
        handleClick();
      } else {
        setAction({
          data: {} as FileFeatureData,
          type: FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
        });
      }
    } else {
      setAction({
        data: {} as FileFeatureData,
        type: hasActiveOnPrem
          ? FILE_TABLE_ACTION_UPLOAD_HIS
          : FILE_TABLE_ACTION_UPLOAD_DRAGNDROP,
      });
      trackEvent({
        file_implementation: "HIS",
        name: TRACK_EVENTS.SELECT_FILE_CTA_CLICKED,
      });
    }
  }, [app, isTablet, hasActiveOnPrem, trackEvent, setAction]);

  useEffect(() => {
    if (processedFile) {
      setAction({
        data: {} as FileFeatureData,
        type: FILE_TABLE_ACTION_UPLOAD,
      });
    }
  }, [processedFile, setAction]);

  return (
    <VerticalLayout height={dp(40)}>
      {uploadButtonProps ? (
        <Button
          {...uploadButtonProps}
          color="primary"
          isDisabled={isDisabled}
          size="sm"
          startContent={<LinkIcon />}
          style={{ margin: 0, zIndex: 0 }}
          variant="light"
          onPress={handleSelectFileV2}
        />
      ) : (
        <RSButton
          color="primary"
          disabled={isDisabled}
          id="upload_file"
          LeftIcon={FileUpIcon}
          loading="na"
          style={{ margin: 0, zIndex: 0 }}
          tooltip={tooltip}
          variant="contained"
          onClick={handleSelectFileV2}
        >
          <HorizontalLayout
            aligned
            {...tourAttributes({
              stepKey: PRODUCT_TOURS.files_page.steps.select_files.key,
              tourKey: PRODUCT_TOURS.files_page.key,
            })}
          >
            <span>
              {translations.fileSection.fileUploadComponent.uploadFile}
            </span>
          </HorizontalLayout>
        </RSButton>
      )}

      <VerticalLayout>
        <input
          ref={inputRef}
          accept={(allowedFileTypes ?? ALLOWED_FILE_MIME_TYPES).join(",")}
          data-testid="file-input"
          disabled={isDisabled}
          name="name"
          style={{ display: "none", visibility: "hidden" }}
          type="file"
          onChange={(event) => handleChange(event.target.files?.[0])}
        />
      </VerticalLayout>
      <>
        <FileUploadHISModal
          availableFiles={availableFiles}
          closeModal={close}
          getFilesHISProgress={getFilesHISProgress}
          isOpen={action?.type === FILE_TABLE_ACTION_UPLOAD_HIS}
          mutationProgress={mutationProgress}
          setAction={setAction}
          unavailableFiles={unavailableFiles}
        />
        <FileUploadModalDragNDrop
          closeModal={close}
          handleChange={handleChange}
          isMessenger={!!isMessenger}
          isOpen={action?.type === FILE_TABLE_ACTION_UPLOAD_DRAGNDROP}
          setAction={setAction}
        />
        <FileUploadModal
          closeModal={close}
          handleSubmit={submit}
          initialValues={{
            category: null,
            fileName: processedFile?.file.name,
            kis_document_id_string: action?.data.kis_document_id_string,
            should_extract_on_upload: showExtractCheckbox,
          }}
          isOpen={action?.type === FILE_TABLE_ACTION_UPLOAD}
          mutationProgress={mutationProgress}
          setAction={setAction}
        />
      </>
    </VerticalLayout>
  );
}
